import React, { useState } from 'react';
import MuiTooltip from '@mui/material/Tooltip';
import MuiButton from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';

export interface TooltipProps {
  title?: React.ReactNode;
  children?: React.ReactElement | React.ReactNode;
  placement?: string;
  overlayClassName?: string;
  color?: string;
  trigger?: string | string[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  [key: string]: any;
}

export const Tooltip: React.FC<TooltipProps> = ({
  title,
  children,
  placement = 'top',
  color,
  trigger,
  enterDelay = 0,
  leaveDelay = 0,
  open: openProp,
  onOpenChange,
  tabIndex: tabIndexProp,
  ...rest
}) => {
  if (children == null || children === false) return null;
  if (title == null || title === false || title === '') {
    return <>{children}</>;
  }
  const mapPlacement = (p: string): 'top' | 'bottom' | 'left' | 'right' => {
    if (p.startsWith('bottom')) return 'bottom';
    if (p.startsWith('left')) return 'left';
    if (p.startsWith('right')) return 'right';
    return 'top';
  };
  const triggers = Array.isArray(trigger) ? trigger : trigger ? [trigger] : ['hover'];
  const clickOnly = triggers.includes('click') && !triggers.includes('hover');
  const [clickOpen, setClickOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : clickOnly ? clickOpen : undefined;

  const setOpen = (next: boolean) => {
    if (!controlled) setClickOpen(next);
    onOpenChange?.(next);
  };

  const child = (
    <span
      role={clickOnly ? 'button' : undefined}
      tabIndex={tabIndexProp !== undefined ? tabIndexProp : clickOnly ? 0 : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', minWidth: 0, cursor: clickOnly ? 'pointer' : undefined }}
      onClick={
        clickOnly
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpen(!(controlled ? openProp : clickOpen));
            }
          : undefined
      }
      onKeyDown={
        clickOnly
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                setOpen(!(controlled ? openProp : clickOpen));
              }
            }
          : undefined
      }
    >
      {children}
    </span>
  );
  const tipBg = color || '#0f172a';
  return (
    <MuiTooltip
      title={title}
      placement={mapPlacement(placement)}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      enterTouchDelay={0}
      arrow
      disableHoverListener={clickOnly}
      disableFocusListener={clickOnly}
      disableTouchListener={clickOnly}
      {...(clickOnly || controlled
        ? {
            open: !!open,
            onClose: () => setOpen(false),
          }
        : null)}
      slotProps={{
        popper: { sx: { zIndex: 14000 } },
        tooltip: {
          sx: {
            bgcolor: tipBg,
            color: '#fff',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.4,
            maxWidth: 340,
            px: 1.25,
            py: 0.75,
            boxShadow: '0 6px 18px rgba(15,23,42,0.28)',
            '& .MuiTooltip-arrow': { color: tipBg },
          },
        },
      }}
      {...rest}
    >
      {child}
    </MuiTooltip>
  );
};

export interface PopconfirmProps {
  title?: React.ReactNode | (() => React.ReactNode);
  onConfirm?: (e?: React.MouseEvent) => void;
  onCancel?: (e?: React.MouseEvent) => void;
  okText?: string;
  cancelText?: string;
  children?: React.ReactElement;
  placement?: string;
  disabled?: boolean;
  [key: string]: any;
}

export const Popconfirm: React.FC<PopconfirmProps> = ({
  title,
  onConfirm,
  onCancel,
  okText = 'OK',
  cancelText = 'Cancel',
  children,
  disabled,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  if (!children) return null;

  const child = React.cloneElement(children, {
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      children.props.onClick?.(e);
      if (!disabled) setAnchor(e.currentTarget);
    },
  });

  const titleNode = typeof title === 'function' ? title() : title;

  return (
    <>
      {child}
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Box sx={{ mb: 1.5 }}>{titleNode}</Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <MuiButton size="small" onClick={(e) => { onCancel?.(e); setAnchor(null); }} sx={{ textTransform: 'none' }}>
              {cancelText}
            </MuiButton>
            <MuiButton
              size="small"
              variant="contained"
              color="primary"
              onClick={(e) => { onConfirm?.(e); setAnchor(null); }}
              sx={{ textTransform: 'none' }}
            >
              {okText}
            </MuiButton>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export interface PopoverProps {
  content?: React.ReactNode | (() => React.ReactNode);
  title?: React.ReactNode | (() => React.ReactNode);
  children?: React.ReactElement;
  trigger?: 'hover' | 'click' | 'focus';
  visible?: boolean;
  open?: boolean;
  onVisibleChange?: (v: boolean) => void;
  placement?: string;
  [key: string]: any;
}

export const AntPopover: React.FC<PopoverProps> = ({
  content,
  title,
  children,
  trigger = 'hover',
  visible,
  open,
  onVisibleChange,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const controlled = visible !== undefined || open !== undefined;
  const isOpen = controlled ? (open ?? visible) : Boolean(anchor);

  if (!children) return null;

  const handlers: any = {};
  if (trigger === 'click') {
    handlers.onClick = (e: React.MouseEvent<HTMLElement>) => {
      children.props.onClick?.(e);
      setAnchor(e.currentTarget);
      onVisibleChange?.(true);
    };
  } else {
    handlers.onMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
      children.props.onMouseEnter?.(e);
      setAnchor(e.currentTarget);
      onVisibleChange?.(true);
    };
    handlers.onMouseLeave = () => {
      if (!controlled) setAnchor(null);
      onVisibleChange?.(false);
    };
  }

  const titleNode = typeof title === 'function' ? title() : title;
  const contentNode = typeof content === 'function' ? content() : content;

  return (
    <>
      {React.cloneElement(children, handlers)}
      <Popover
        open={!!isOpen && Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => { setAnchor(null); onVisibleChange?.(false); }}
      >
        <Box sx={{ p: 1.5, maxWidth: 360 }}>
          {titleNode && <Box sx={{ fontWeight: 600, mb: 1 }}>{titleNode}</Box>}
          {contentNode}
        </Box>
      </Popover>
    </>
  );
};

export interface TagProps {
  color?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  className?: string;
  closable?: boolean;
  onClose?: (e: React.MouseEvent) => void;
  icon?: React.ReactNode;
  [key: string]: any;
}

const TAG_COLOR_MAP: Record<string, { bgcolor: string; color: string }> = {
  success: { bgcolor: '#16a34a', color: '#fff' },
  green: { bgcolor: '#16a34a', color: '#fff' },
  error: { bgcolor: '#dc2626', color: '#fff' },
  red: { bgcolor: '#dc2626', color: '#fff' },
  warning: { bgcolor: '#d97706', color: '#fff' },
  orange: { bgcolor: '#d97706', color: '#fff' },
  processing: { bgcolor: '#2563eb', color: '#fff' },
  blue: { bgcolor: '#2563eb', color: '#fff' },
  default: { bgcolor: '#e2e8f0', color: '#1e293b' },
}

export const Tag: React.FC<TagProps> = ({ color, children, style, className, closable, onClose, icon }) => {
  const mapped =
    (color && TAG_COLOR_MAP[color]) ||
    (color && color !== 'default' && color.startsWith('#')
      ? { bgcolor: color, color: '#fff' }
      : TAG_COLOR_MAP.default)

  return (
    <Chip
      size="small"
      label={children}
      icon={icon as any}
      className={className}
      onDelete={closable ? onClose : undefined}
      sx={{
        '& .MuiChip-label': { px: '4px', py: 0, lineHeight: '16px' },
        height: 16,
        fontSize: 9,
        fontWeight: 700,
        borderRadius: '2px',
        bgcolor: mapped.bgcolor,
        color: mapped.color,
        ...style,
      }}
    />
  )
}

export { AntPopover as Popover };
